import { useMemo, useState } from "react";
import type { Project } from "@proof/core";
import { useAuthoringServices } from "./services";
import {
  Select,
  Button,
  Control,
  Modal,
  SearchableSelect,
  type SelectLoader,
} from "./components";

export function DocumentFormSource({
  project,
  label = "Generate form from document",
  onSelect,
}: {
  project: Project;
  label?: string;
  onSelect(project: Project, linked: boolean): void;
}) {
  const services = useAuthoringServices();
  const [open, setOpen] = useState(false),
    [id, setId] = useState(project.id),
    [names, setNames] = useState<Record<string, string>>({}),
    [linked, setLinked] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const current = `${project.name} (current editor)`;
  // Search runs inside the dropdown against the host's project list.
  const load = useMemo<SelectLoader>(() => {
    const cache = new Map<string, Promise<{ id: string; name: string }[]>>();
    return async (query, offset, limit, signal) => {
      const key = query.trim();
      if (!cache.has(key))
        cache.set(
          key,
          Promise.resolve(
            services.listFormProjects?.(key, signal) ?? [],
          ).catch((cause) => {
            cache.delete(key);
            throw cause;
          }),
        );
      const rows = [
        ...(!key || current.toLowerCase().includes(key.toLowerCase())
          ? [{ id: project.id, name: current }]
          : []),
        ...(await cache.get(key)!).filter((row) => row.id !== project.id),
      ];
      setNames((known) => ({
        ...known,
        ...Object.fromEntries(rows.map((row) => [row.id, row.name])),
      }));
      return {
        items: rows
          .slice(offset, offset + limit)
          .map((row) => ({ value: row.id, label: row.name })),
        total: rows.length,
      };
    };
  }, [services.listFormProjects, project.id, current]);
  return (
    <>
      <Button
        onClick={() => {
          setError("");
          setOpen(true);
        }}
      >
        {label}
      </Button>
      {open && (
        <Modal
          title="Generate form from document"
          onClose={() => !busy && setOpen(false)}
        >
          <Control label="Base this form on">
            <SearchableSelect
              label="Base this form on"
              value={id}
              onChange={setId}
              load={load}
              selectedLabel={id === project.id ? current : names[id]}
              searchPlaceholder="Search source projects…"
            />
          </Control>
          <Control label="Future changes">
            <Select
              value={linked ? "linked" : "copy"}
              onChange={(event) => setLinked(event.target.value === "linked")}
            >
              <option value="copy">Copy — assign to projects manually</option>
              <option value="linked">
                Link this source project — follow future form changes
              </option>
            </Select>
          </Control>
          <p>
            {linked
              ? "Saving later versions of this global form updates matching input settings in the linked project's draft. Published proofs retain their saved versions until the project is published again. Document bindings and rules stay with the project."
              : "The generated form is independent. Saving it will not update the source project."}
          </p>
          {error && <p role="alert">{error}</p>}
          <div className="button-row">
            <Button disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={busy || !id}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  const source =
                    id === project.id
                      ? project
                      : await services.loadFormProject?.(
                          id,
                          new AbortController().signal,
                        );
                  if (!source || source.tenantId !== project.tenantId)
                    throw new Error(
                      "Source project is unavailable in this workspace.",
                    );
                  if (
                    linked &&
                    !window.confirm(
                      `Future changes to this form will update matching fields in ${source.name}'s draft. Continue?`,
                    )
                  )
                    return;
                  onSelect(source, linked);
                  setOpen(false);
                } catch (error) {
                  setError((error as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Loading…" : "Generate Form"}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
